<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <section v-if="ready">
      <component :is="layout">
        <router-view />
      </component>
    </section>
  </div>
</template>

<script>

  // This will be populated in `beforeCreate` hook
  import { mapState } from 'vuex';
  import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
  import { provideToast } from 'vue-toastification/composition'
  import { watch } from '@vue/composition-api'
  import useAppConfig from '@core/app-config/useAppConfig'

  import { useWindowSize, useCssVar } from '@vueuse/core'

  import store from '@/store'
  import useJwt from '@/auth/jwt/useJwt';
  import PrisonService from './services/PrisonService';

  const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
  const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
  const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

  export default {
    components: {
      // Layouts
      LayoutHorizontal,
      LayoutVertical,
      LayoutFull,
    },
    data() {
      return {
        ready: false,
        idleWarnId: null,
        idleTimerId: null,
        idleTimeAllowed: 15,
      }
    },
    // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
    // Currently, router.currentRoute is not reactive and doesn't trigger any change
    computed: {
      ...mapState({
        user: state => state.auth.user,
      }),
      layout() {
        if (this.$route.meta.layout === 'full') return 'layout-full'
        return `layout-${this.contentLayoutType}`
      },
      contentLayoutType() {
        return this.$store.state.appConfig.layout.type
      },
    },
    mounted() {
      this.$store.dispatch('setRegionFromLocal'); // set region
      setTimeout(() => {
        this.ready = true;
      });
      setTimeout(() => {
        if (this.user) {
          this.fetchPrisonAndSetUpIdleTimer()
        }
      }, 1500);
    },
    beforeCreate() {
      // Set colors in theme
      const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

      // eslint-disable-next-line no-plusplus
      for (let i = 0, len = colors.length; i < len; i++) {
        $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
      }

      // Set Theme Breakpoints
      const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

      // eslint-disable-next-line no-plusplus
      for (let i = 0, len = breakpoints.length; i < len; i++) {
        $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
      }

      // Set RTL
      const { isRTL } = $themeConfig.layout
      document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

      this.$store.dispatch('auth/setCurrentUserFromLocalStorage');
    },
    methods: {
      async fetchPrisonAndSetUpIdleTimer() {
        try {
          const { data: prison } = await PrisonService.getPrison()
          console.log('>> session timeout: ', prison.session_timeout)
          console.log('>> session timeout: ', prison.session_length_mins)
          if (prison.session_timeout) {
            this.idleTimeAllowed = parseInt(prison.session_length_mins, 10);
            this.trackIdleTime();
            this.detectUserActivity();
          }
        } catch (err) {
          console.error('Error getting prison idle time: ', err);
        }
      },
      detectUserActivity() {
        document.addEventListener('click', this.trackIdleTime);
        document.addEventListener('mousemove', this.trackIdleTime);
      },
      trackIdleTime() {
        if (!this.user) {
          return
        }
        if (this.idleTimerId) {
          clearTimeout(this.idleTimerId);
          clearTimeout(this.idleWarnId);
        }

        // 30 seconds before the end
        this.idleWarnId = setTimeout(() => {
          // Show the logout warning modal
          this.showInactivityWarning();
        }, ((this.idleTimeAllowed * 1000 * 60) - (30 * 1000)))

        this.idleTimerId = setTimeout(() => {
          // Log user out
          this.logout()
        }, this.idleTimeAllowed * 1000 * 60);
      },
      showInactivityWarning() {
        this.$toast.warning('Warning: You will be logged out shortly unless activity is detected', {
          timeout: 30000,
        })
      },
      logout() {
        // Remove userData from localStorage
        // ? You just removed token from localStorage. If you like, you can also make API call to backend to blacklist used token
        localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
        localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

        // Remove userData from localStorage
        localStorage.removeItem('userData')

        // Redirect to login page
        this.$router.push({ name: 'login' })

        this.$store.dispatch('auth/clearLocalUser')
        this.$store.dispatch('auth/setLocalUser', null)
      },
    },
    setup() {
      const { skin, skinClasses } = useAppConfig()

      // If skin is dark when initialized => Add class to body
      if (skin.value === 'dark') document.body.classList.add('dark-layout')

      // Provide toast for Composition API usage
      // This for those apps/components which uses composition API
      // Demos will still use Options API for ease
      provideToast({
        hideProgressBar: true,
        closeOnClick: false,
        closeButton: false,
        icon: false,
        timeout: 3000,
        transition: 'Vue-Toastification__fade',
      })

      // Set Window Width in store
      store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
      const { width: windowWidth } = useWindowSize()
      watch(windowWidth, val => {
        store.commit('app/UPDATE_WINDOW_WIDTH', val)
      })

      return {
        skinClasses,
      }
    },
  }
</script>
